<template>
  <v-card>
    <v-card-title>
      <v-row no-gutters>
        {{ timesheet && timesheet.id ? "Edit" : "Add" }} Timesheet
        <v-spacer></v-spacer>
        <v-btn @click="cancel" color="red" size="small" icon="mdi-close">
          <v-icon></v-icon>
        </v-btn>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="3" sm="3" class="p-0">
          <TimesheetSideBox
            v-if="!pageLoading || !regLoading"
            :date="formatedSelectedDate"
            :teamMemberInfo="teamMemberInfo"
            :key="`s-v-${sideBoxVersion}`"
            :commitmentColors="commitmentColors"
            @editTimesheet="editTimesheet"
          />
        </v-col>
        <v-col cols="12" md="9" sm="9" class="p-0">
          <v-form ref="frmMain" v-model="valid" lazy-validation class="form-inputs">
            <v-card :loading="pageLoading || loading" :disabled="pageLoading || loading">
              <v-card-text>
                <v-row class="details-container mb-0 pb-0">
                  <v-col cols="12" md="4" sm="6">
                    <label class="input-label"> Date </label>
                    <v-date-input
                      :min="minDate"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock"
                      first-day-of-week="1"
                      :rules="[rules.required]"
                      v-model="timesheetModel.date"
                      hide-actions
                      placeholder="Date"
                      prepend-icon=""
                    />
                  </v-col>
                  <v-col cols="12" md="4" sm="6">
                    <TextField
                      id="timesheet_register_ticket"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock"
                      :rules="[rules.required]"
                      @blur="fillDescription()"
                      placeholder="Ticket / Issue No"
                      label="Ticket / Issue No"
                      v-model="timesheetModel.issueNo"
                      outlined
                    />
                  </v-col>
                  <v-col cols="12" md="4" sm="6">
                    <NumberField
                      id="timesheet_register_effort"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock"
                      :rules="[rules.required, rules.step, rules.notZero]"
                      min="0"
                      max="10"
                      placeholder="Effort / Hours"
                      label="Effort / Hours"
                      v-model="timesheetModel.effort"
                      outlined
                      step="0.25"
                      showNumberIncSuggestion
                    />
                  </v-col>
                  <v-col cols="12" md="6" sm="6">
                    <SelectField
                      id="timesheet_register_commitment"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock"
                      :rules="[rules.required]"
                      label="Commitment"
                      :items="commitments"
                      placeholder="Commitment"
                      item-title="Name"
                      item-value="id"
                      v-model="timesheetModel.commitmentId"
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row class="details-container mt-0 pt-0">
                  <v-col cols="12" md="6" sm="6">
                    <SelectField
                      id="timesheet_register_project"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock || projectLoading"
                      :rules="[rules.required]"
                      label="Project"
                      placeholder="Project"
                      :items="projects"
                      item-title="Name"
                      item-value="id"
                      v-model="timesheetModel.projectId"
                      outlined
                      :loading="projectLoading"
                    />
                  </v-col>
                  <v-col cols="10" md="5" sm="10">
                    <AutoCompleteField
                      id="timesheet_register_epic"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock || epicLoading"
                      :rules="[rules.required]"
                      label="Epic"
                      placeholder="Epic"
                      v-model="timesheetModel.epic"
                      :items="epicList"
                      item-title="Name"
                      item-value="Name"
                      :loading="epicLoading"
                      outlined
                    />
                  </v-col>
                  <v-col class="pl-0 pr-0 align-self-center" cols="2" md="1" sm="2">
                    <v-btn
                      v-if="!timesheetModel.isLock"
                      class="mx-2"
                      size="small"
                      color="red"
                      @click="showEpic = true"
                      :disabled="!timesheetModel.projectId"
                      icon="mdi-plus"
                    ></v-btn>
                    <v-tooltip location="top" max-width="500">
                      <template v-slot:activator="{ props }">
                        <span class="tooltipIcon mt-1" v-bind="props"> ? </span>
                      </template>
                      <p>
                        Epics are large bodies of work that can be broken down to a number of smaller tasks or stories. Setting the right Epic makes
                        our reporting a lot more meaningful. Some Epic examples could be:
                        <span class="boldTooltip"
                          >Tech Debt, Onboarding, March 2050 Launch, Version x, Feature ABC, Misc, Security, Performance Improvements.
                        </span>
                      </p>
                      <p>
                        There is no right or wrong way of choosing our Epics. The main point is to be able to produce high level reports on large
                        bodies of work that the management will be interested in. For example, how much time this year was spent on
                        <span class="boldTooltip">Feature ABC</span>, or <span class="boldTooltip">Tech Debt</span>.
                      </p>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="12" md="6" sm="6">
                    <SelectField
                      id="timesheet_register_activity"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock || epicLoading"
                      :rules="[rules.required]"
                      placeholder="Activity"
                      label="Activity"
                      v-model="timesheetModel.activityId"
                      :items="activityList"
                      item-title="Value"
                      item-value="id"
                      outlined
                      v-on:change="activityChanged"
                    />
                  </v-col>
                  <v-col cols="10" md="5" sm="10">
                    <SelectField
                      id="timesheet_register_technology"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock || epicLoading"
                      :rules="[rules.required]"
                      label="Technology"
                      placeholder="Technology"
                      v-model="timesheetModel.technologyId"
                      :items="technologyList"
                      item-title="Value"
                      item-value="id"
                      outlined
                    />
                  </v-col>
                  <v-col class="align-self-center pl-0 pr-0" cols="2" md="1" sm="2">
                    <v-btn v-if="!timesheetModel.isLock" class="mx-2" size="small" color="red" @click="showTechnology = true" icon="mdi-plus"></v-btn>
                  </v-col>
                  <v-col cols="12" md="12">
                    <TextAreaField
                      id="timesheet_register_description"
                      :readonly="timesheetModel.isLock"
                      :disabled="timesheetModel.isLock || epicLoading"
                      :rules="[rules.required]"
                      outlined
                      placeholder="Description"
                      label="Description"
                      v-model="timesheetModel.description"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions class="action-bottons ml-3">
                <p class="mb-0" v-if="timesheetModel.isLock">{{ timesheetModel.message }}</p>
                <div class="d-flex flex-wrap flex-column flex-md-row-reverse ml-auto mr-auto mr-md-1 ga-3">
                  <v-btn
                    v-if="!timesheetModel.isLock"
                    class="tertiary_btn right actionButton"
                    :loading="loading"
                    :disabled="loading"
                    @click="confirm(true)"
                    ><v-icon>mdi-plus</v-icon>Save and add another</v-btn
                  >
                  <v-btn
                    id="timesheet_register_save_button"
                    v-if="!timesheetModel.isLock"
                    class="primary_btn right actionButton"
                    :loading="loading"
                    :disabled="loading"
                    @click="confirm(false)"
                    ><v-icon>mdi-check</v-icon>Save</v-btn
                  >
                  <v-btn class="secondary_btn right actionButton" @click="cancel"> <v-icon>mdi-close</v-icon>Cancel</v-btn>
                </div>
              </v-card-actions>
            </v-card>
            <v-dialog :scrollable="false" v-if="selectedProject && showEpic" v-model="showEpic" persistent max-width="600px">
              <add-epic
                :epicList="epicList"
                :project="selectedProject"
                :epicLoading="epicLoading"
                @cancel="showEpic = false"
                @success="epicAdded"
              ></add-epic>
            </v-dialog>
            <v-dialog persistent :scrollable="false" max-width="600px" v-model="showTechnology">
              <add-technology
                :activities="activityList"
                :technologies="fullTechnologyList"
                @cancel="showTechnology = false"
                @confirm="technologyAdded"
              ></add-technology>
            </v-dialog>
          </v-form>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import LoadingComponent from "../../components/Loading.vue";
import AddEpic from "./AddEpic.vue";
import Timesheet from "../../models/Timesheet";
import Utils from "../../utils/Utils";
import AddTechnology from "./AddTechnology.vue";
import { Technology } from "../../models/Technology";
import { Activity } from "../../models/Activity";
import Project from "../../models/Project";
import TimesheetSideBox from "./TimesheetSideBox.vue";
import TextField from "../../components/Fields/TextField.vue";
import SelectField from "../../components/Fields/SelectField.vue";
import TextAreaField from "../../components/Fields/TextAreaField.vue";
import AutoCompleteField from "../../components/Fields/AutoCompleteField.vue";
import ActivityService from "../../services/ActivityService";
import EpicService from "../../services/EpicService";
import TechnologyService from "../../services/TechnologyService";
import rules from "../../utils/validations";
import { CommitmentModel, CommitmentModelStatusV2Enum } from "../../services/openApi";
import moment from "moment";

export default defineComponent({
  components: {
    AddEpic,
    AddTechnology,
    LoadingComponent,
    TimesheetSideBox,
    TextField,
    SelectField,
    TextAreaField,
    AutoCompleteField,
  },
  props: ["isAdmin", "teamMemberInfo", "date", "tmCommitments", "timesheet", "userProjects", "regLoading", "commitmentColors"],
  data() {
    return {
      sideBoxVersion: 0,
      pageLoading: true,
      valid: false,
      menu: false,
      timesheetModel: {
        id: this.timesheet.id,
        date: Utils.vsDateToDatetime(this.timesheet && this.timesheet.Date ? this.timesheet.Date : this.date), // this.$route.params.date || "",
        issueNo: "",
        effort: 0,
        description: "",
        commitmentId: "",
        projectId: "",
        epic: "",
        activityId: "",
        technologyId: "",
        isLock: false,
        message: "",
      },
      showEpic: false,
      projectLoading: false,
      epicLoading: false,
      projects: [] as Project[],
      epicList: [] as string[],
      technologyList: [] as Technology[],
      fullTechnologyList: [] as Technology[],
      activityList: [] as Activity[],
      fullActivityList: [] as Activity[],
      loading: false,
      showTechnology: false,
      rules,
    };
  },
  methods: {
    resetModel() {
      this.timesheetModel.id = "";
      this.timesheetModel.issueNo = "";
      this.timesheetModel.effort = 0;
      this.timesheetModel.description = "";
      this.epicLoading = false;
    },
    async fetchUserProjects() {
      this.fetchProjects();
    },
    activityChanged() {
      this.sortTechnologies();
      if (this.timesheetModel.technologyId && this.timesheetModel.technologyId.length > 0) {
        let selectedTechnology = this.technologyList.find((item) => item.id == this.timesheetModel.technologyId);
        if (selectedTechnology && (selectedTechnology as any).disabled) {
          this.timesheetModel.technologyId = "";
        }
      }
      var selectedActivity = this.activityList.find((c) => c.id === this.timesheetModel.activityId);
      if (selectedActivity && selectedActivity.DefaultTechnology) {
        this.timesheetModel.technologyId = selectedActivity.DefaultTechnology;
      } else {
        this.timesheetModel.technologyId = "";
      }
    },
    cancel() {
      this.$emit("cancel", this.timesheetModel.date);
    },
    technologyAdded(model: any): void {
      this.technologyList.push(model);
      if (this.timesheetModel.activityId != "" && model.ActivityIds && model.ActivityIds.indexOf(this.timesheetModel.activityId) != -1) {
        this.timesheetModel.technologyId = model.id;
      }
      this.showTechnology = false;
    },
    async fetchTimesheet() {
      await this.$emit("fetchTimesheet");
    },
    fillDescription() {
      if (this.timesheetModel.issueNo) {
        this.timesheetModel.description = this.timesheetModel.issueNo + " - ";
      } else {
        this.timesheetModel.description = "";
      }
    },
    fetchProjects() {
      if (this.timesheetModel.commitmentId) {
        let projectIds = this.tmCommitments.filter((x: any) => x.id === this.timesheetModel.commitmentId)[0].ProjectIds;
        this.projects = this.userProjects.filter((item: any) => projectIds.includes(item.id));
        if (this.timesheetModel.projectId && !this.projects.find((item) => item.id == this.timesheetModel.projectId)) {
          this.timesheetModel.projectId = "";
        }
      } else {
        this.projects = [] as Project[];
        this.timesheetModel.projectId = "";
      }
    },
    async fetchEpics(prId: string) {
      this.epicList = [];
      if (prId && prId != "") {
        const epic = await EpicService.getEpicOfProject(prId || "");

        if (epic) {
          epic.Items.forEach((item) => {
            this.epicList.push(item.Name);
          });
          if (this.timesheetModel.epic && this.timesheetModel.epic != "" && !this.epicList.find((item) => item == this.timesheetModel.epic)) {
            this.timesheetModel.epic = "";
          }
        } else {
          this.timesheetModel.epic = "";
        }
      } else {
        this.timesheetModel.epic = "";
      }
      this.showEpic = false;
      this.epicLoading = false;
    },
    async epicAdded(model: any) {
      this.epicLoading = true;
      var epics = await EpicService.addEpic(model.epic, model.projectId);
      this.epicList = [];
      if (epics) {
        epics.Items.forEach((item) => {
          this.epicList.push(item.Name);
        });
        var selectedEpic = this.epicList.find((c) => c === model.epic);
        if (selectedEpic) {
          this.timesheetModel.epic = selectedEpic;
        }
      }
      this.epicLoading = false;
      this.showEpic = false;
    },

    sortTechnologies() {
      this.technologyList.forEach((item) => {
        if (this.timesheetModel.activityId == "" || !item.ActivityIds || item.ActivityIds.indexOf(this.timesheetModel.activityId) == -1) {
          (item as any).disabled = true;
        } else {
          (item as any).disabled = false;
        }
      });
      this.technologyList.sort((a, b) => {
        if ((a as any).disabled > (b as any).disabled) return 1;
        if ((a as any).disabled < (b as any).disabled) return -1;
        if (a.Value.toLowerCase() < b.Value.toLowerCase()) {
          return -1;
        }
        if (a.Value.toLowerCase() > b.Value.toLowerCase()) {
          return 1;
        }
        return 0;
      });
    },
    editTimesheet(item: any) {
      this.$emit("editTimesheet", item);
    },

    async fetchTechnologies(activityId = "") {
      this.fullTechnologyList = await TechnologyService.getTechnologyList();
      if (!activityId) {
        this.technologyList = this.fullTechnologyList;
      } else {
        this.technologyList = this.fullTechnologyList.filter((c) => c.ActivityIds && c.ActivityIds.includes(activityId));
      }
      this.sortTechnologies();
    },
    async fetchActivities() {
      this.fullActivityList = [];
      this.fullActivityList = await ActivityService.getActivityList();
      // this.filterActivity();
    },
    resetHours(value: any) {
      value.setHours(0, 0, 0, 0);
      return value;
    },
    async confirm(addAfterSave: any) {
      const isValid = (await (this.$refs.frmMain as any).validate()).valid;
      if (isValid) {
        if (this.timesheetModel.effort < 0) {
          this.timesheetModel.effort = 0;
        }
        const timesheet = {
          id: this.timesheetModel.id,
          CommitmentId: this.timesheetModel.commitmentId,
          ActivityId: this.timesheetModel.activityId,
          Epic: this.timesheetModel.epic,
          Date: this.timesheetModel.date,
          DateLink: null,
          Description: this.timesheetModel.description,
          Effort: Number(this.timesheetModel.effort),
          TeamMemberId: this.teamMemberInfo.Id,
          IssueNo: this.timesheetModel.issueNo,
          ProjectId: this.timesheetModel.projectId,
          TechnologyId: this.timesheetModel.technologyId,
        } as Timesheet;
        await this.$emit("confirmTimesheet", { timesheet, addAfterSave });
      }
    },
    checkDate(): boolean {
      if (this.date && !this.isAdmin) {
        const dt = Utils.vsDateToDatetime(this.date);
        dt.setHours(0);
        dt.setMinutes(0);
        dt.setSeconds(0);
        dt.setMilliseconds(0);
        return dt >= this.dateLimit;
      }
      return true;
    },
    filterActivity() {
      this.activityList = this.fullActivityList.filter(
        (item) =>
          item.Disabled == false ||
          (item.Disabled == true &&
            this.timesheetModel.activityId &&
            this.timesheetModel.activityId.length > 1 &&
            item.id == this.timesheetModel.activityId)
      );
    },
    setTimesheet() {
      if (this.timesheet) {
        this.timesheetModel = {
          id: this.timesheet.id,
          date: Utils.vsDateToDatetime(this.timesheet && this.timesheet.Date ? this.timesheet.Date : this.date),
          issueNo: this.timesheet.IssueNo,
          effort: this.timesheet.Effort,
          description: this.timesheet.Description,
          commitmentId: this.timesheet.CommitmentId,
          projectId: this.timesheet.ProjectId,
          epic: this.timesheet.Epic,
          activityId: this.timesheet.ActivityId,
          technologyId: this.timesheet.TechnologyId,
          isLock: this.timesheet.IsLocked,
          message: this.timesheet.Message,
        };
      }
      this.filterActivity();
      this.sortTechnologies();
    },
  },
  computed: {
    dateLimit(): Date {
      return Utils.GetLimitedDateForTimesheet(this.isAdmin);
    },
    minDate(): string {
      return Utils.toVsDateFormat(this.dateLimit);
    },
    commitments(): CommitmentModel[] {
      const udt = Utils.getCurrentUtcDate();
      udt.setHours(0, 0, 0, 0);
      const retVal = this.timesheetModel.date
        ? this.tmCommitments.filter(
            (x: any) =>
              (!x.EndDate || this.resetHours(moment(x.EndDate).toDate()) >= (this.resetHours(this.timesheetModel.date) || udt)) &&
              (!x.StartDate || this.resetHours(moment(x.StartDate).toDate()) <= (this.resetHours(this.timesheetModel.date) || udt))
          )
        : this.tmCommitments;
      if (this.timesheetModel.id && this.timesheetModel.commitmentId) {
        const oldCommitment = this.tmCommitments.find((c: any) => c.id === this.timesheetModel.commitmentId);
        if (oldCommitment) {
          if (!retVal.find((c: any) => c.id == this.timesheetModel.commitmentId)) retVal.push(oldCommitment);
        }
      }
      return retVal;
    },
    formatedSelectedDate() {
      return Utils.toVsDateFormat(this.timesheetModel.date || new Date());
    },
    selectedCommitment(): { id: string; Name: string } | null {
      if (this.timesheetModel.commitmentId) {
        const item = this.tmCommitments.filter((x: any) => x.id === this.timesheetModel.commitmentId || "")[0];
        return { id: item.id, Name: item.Name };
      } else {
        return null;
      }
    },
    selectedProject(): any {
      const projects = this.projects.filter((x) => x.id === this.timesheetModel.projectId);
      if (projects && projects.length > 0) {
        const retVal = { Name: "", id: this.timesheetModel.projectId };
        retVal.Name = projects[0].Name;
        return retVal;
      } else {
        return null;
      }
    },
  },
  watch: {
    date(newVal: string) {
      if (this.checkDate() === false) {
        this.$emit("errorRaised", "Date is out of range (max 6 days ago)");
      }
    },
    "timesheetModel.commitmentId"(newVal) {
      this.projectLoading = true;
      this.fetchProjects();
      this.projectLoading = false;
    },
    "timesheetModel.effort"(newVal) {
      if (newVal > 10) {
        this.timesheetModel.effort = 10;
      }
      if (newVal < 0.0) {
        this.timesheetModel.effort = 0;
      }
    },
    "timesheetModel.projectId"(newVal) {
      this.epicLoading = true;
      this.fetchEpics(newVal);
      this.epicLoading = false;
    },
    "timesheetModel.activityId"(newVal) {
      this.fetchTechnologies(newVal);
    },
    regLoading(newVal) {
      this.loading = newVal;
      if (!newVal) {
        this.sideBoxVersion++;
        this.resetModel();
      }
    },
  },
  async mounted() {
    await this.fetchTechnologies();
    await this.fetchActivities();
    this.setTimesheet();
    await this.fetchUserProjects();
    this.pageLoading = false;
    if (this.checkDate() === false) {
      this.$emit("errorRaised", "Date is out of range (max 6 days ago)");
      this.$emit("cancel");
    }
    if (!this.timesheetModel.description && this.timesheetModel.issueNo) {
      this.timesheetModel.description = this.timesheetModel.issueNo + " -";
    }
  },
});
</script>

<style lang="scss" scoped>
.details-container {
  padding: 20px;
}
.boldTooltip {
  font-weight: bold;
}
.tooltipIcon {
  position: absolute;
}

.panel-item {
  max-height: 760px;
}

@media (max-width: 960px) {
  .action-bottons {
    height: auto !important;
  }
}
</style>
